import { zodResolver } from "@hookform/resolvers/zod";

import { checkPasswordComplexity } from "check-password-complexity";
import { Box, Checkbox, Typography } from "@mui/material";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import classNames from "classnames";

import { useDispatch } from "react-redux";

import { TokenPurpose } from "@/enum";

import { ROUTE_CONFIG } from "@/routes/config";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import { ETokenStatus } from "@/enum/ETokenStatus";

import { UserLanguage } from "@/enum/user.enum";

import i18n from "@/i18n";

import { Enable2FAModal } from "@/pages/Private/pages/Account/components/Enable2FAModal";

import FormContainer from "../../components/FormContainer";
import { useAcceptInvitationMutation } from "../../redux/auth.api";
import { AcceptInvitationSchema, AcceptInvitationType } from "./schema/set-password";
import { confirmAuth } from "../../redux/auth.slice";

export const AcceptInvitation: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`basics.${key}`);
	const [error, setError] = useState<string | null>(null);

	const [searchParams] = useSearchParams();

	const [passwordComplexity, setPasswordComplexity] = useState<number>(0);

	const [acceptInvitation, { isLoading, error: reqError }] = useAcceptInvitationMutation();
	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isLoading: isFormLoading },
	} = useForm<AcceptInvitationType>({
		defaultValues: {
			firstName: "",
			lastName: "",
			password: "",
			confirmPassword: "",
			userLanguage:
				(i18n.language.substring(0, 2).toUpperCase() as UserLanguage) || UserLanguage.DE,
			termsAndConditions: false,
		},
		resolver: zodResolver(AcceptInvitationSchema),
	});

	const formValues = watch();

	useEffect(() => {
		if (formValues.password) {
			const complexity = checkPasswordComplexity(formValues.password);

			setPasswordComplexity(complexity.score);
		} else {
			setPasswordComplexity(0);
		}
	}, [formValues.password]);

	const passwordIncludesNumbers = useMemo(() => {
		return formValues.password.match(/\d/);
	}, [formValues.password]);

	const passwordIncludesSpecialCharacters = useMemo(() => {
		return formValues.password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/);
	}, [formValues.password]);

	const passwordIncludesUppercase = useMemo(() => {
		return formValues.password.match(/[A-Z]/);
	}, [formValues.password]);

	const passwordIncludesLowercase = useMemo(() => {
		return formValues.password.match(/[a-z]/);
	}, [formValues.password]);

	const tileColor = useMemo(() => {
		if (passwordComplexity >= 5) {
			return "bg-status-success";
		} else if (passwordComplexity >= 3) {
			return "bg-status-warning";
		} else if (passwordComplexity >= 1) {
			return "bg-status-error";
		}

		return "bg-gray-200";
	}, [passwordComplexity]);

	const [is2FAEnableModalOpen, setIs2FAEnableModalOpen] = useState<boolean>(false);

	const [qrCode, setQrCode] = useState<string>("");

	const dispatch = useDispatch();

	const onSubmit = async (values: AcceptInvitationType) => {
		try {
			const result = await acceptInvitation({
				token: searchParams.get("token") || "",
				purpose: TokenPurpose.INVITATION,
				password: values.password,
				confirmPassword: values.confirmPassword,
				userLanguage: values.userLanguage,
				firstName: values.firstName,
				lastName: values.lastName,
			}).unwrap();

			if (result?.status === ETokenStatus.USED) {
				if (result?.qrCode) {
					setQrCode(result.qrCode);
					setIs2FAEnableModalOpen(true);
				}
			} else if (result?.status === ETokenStatus.EXPIRED) {
				setError(ts("invitation-expired"));
			} else if (result?.status === ETokenStatus.INVALID) {
				setError(ts("invitation-token-invalid"));
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleModalClose = () => {
		setIs2FAEnableModalOpen(false);
		dispatch(confirmAuth());
	};

	return (
		<div className="w-full h-full flex items-center justify-center">
			<FormContainer
				description={ts("confirm-invite-description")}
				disabled={passwordComplexity < 5}
				formContainerClassName="self-center"
				formError={reqError ?? error}
				formFooter={
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Checkbox
							sx={errors?.termsAndConditions?.message ? { color: "red" } : {}}
							{...register("termsAndConditions")}
						/>

						<Trans
							components={{
								noLink: (
									<Typography color="text.secondary" component="p" ml={0.5} variant="body2" />
								),
								link1: (
									<Typography
										color="primary"
										component="a"
										href={ROUTE_CONFIG.PRIVACY_POLICY}
										rel="noreferrer"
										sx={{ ml: 0.5, textDecoration: "none" }}
										target="_blank"
										variant="subtitle2"
									/>
								),
								noLink2: (
									<Typography color="text.secondary" component="p" ml={0.5} variant="body2" />
								),
							}}
							i18nKey="basics.privacyPolicy"
						/>
					</Box>
				}
				handleSubmit={handleSubmit(onSubmit)}
				isLoading={isLoading}
				submitButtonText={ts("confirm")}
				title={ts("accepting-invitation")}
			>
				<FormTextField
					autoComplete="firstName"
					errorMessage={errors?.firstName?.message}
					formRegister={register("firstName")}
					label={ts("firstName")}
					placeHolder={ts("enter-your-firstname")}
				/>
				<FormTextField
					autoComplete="lastName"
					errorMessage={errors?.lastName?.message}
					formRegister={register("lastName")}
					label={ts("lastName")}
					placeHolder={ts("enter-your-lastname")}
				/>
				<FormTextField
					autoComplete="password"
					errorMessage={errors?.password?.message}
					formRegister={register("password")}
					label={ts("password")}
					placeHolder={ts("enter-your-password")}
					type="password"
					onChange={(e) => {
						setValue("password", e.target.value);
					}}
				/>
				{formValues.password && formValues.password?.length > 0 && (
					<>
						<div className="flex flex-row gap-2">
							<div
								className={classNames(
									"w-1/3 h-[3px] bg-gray-200 rounded-md",
									passwordComplexity >= 1 ? tileColor : "bg-gray-200"
								)}
							></div>
							<div
								className={classNames(
									"w-1/3 h-[3px] bg-gray-200 rounded-md",
									passwordComplexity >= 3 ? tileColor : "bg-gray-200"
								)}
							></div>
							<div
								className={classNames(
									"w-1/3 h-[3px] bg-gray-200 rounded-md",
									passwordComplexity >= 5 ? tileColor : "bg-gray-200"
								)}
							></div>
						</div>

						<div className="text-left text-xs font-normal w-full mb-4">
							{formValues.password?.length < 8 && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.minimum")}
								</div>
							)}
							{!passwordIncludesNumbers && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.number")}{" "}
								</div>
							)}
							{!passwordIncludesSpecialCharacters && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.specialChar")}{" "}
								</div>
							)}
							{!passwordIncludesUppercase && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.uppercase")}{" "}
								</div>
							)}
							{!passwordIncludesLowercase && (
								<div className="text-left text-xs font-normal w-full mb-1">
									{t("basics.passwordGuidelines.lowercase")}{" "}
								</div>
							)}
						</div>
					</>
				)}

				<FormTextField
					autoComplete="confirmPassword"
					errorMessage={errors?.confirmPassword?.message}
					formRegister={register("confirmPassword")}
					label={ts("confirmPassword")}
					placeHolder={ts("enter-your-confirm-password")}
					type="password"
				/>
			</FormContainer>
			{is2FAEnableModalOpen && (
				<Enable2FAModal
					isLoading={isFormLoading}
					isOpen={is2FAEnableModalOpen}
					isRegistration={true}
					qrCode={qrCode as string}
					setIsOpen={setIs2FAEnableModalOpen}
					onClose={handleModalClose}
				/>
			)}
		</div>
	);
};
